import { ExperimentFlag } from '../enums/ExperimentFlag'
import { Experiment, IExperiment } from './Experiment'
import { getQueryParam } from './getQueryParam'

export function transformToIExperiments(
  defaultVersion: string,
  betaGroups: Array<IBetaGroup>,
  slug: string
): Array<IExperiment> {
  const flag = 'version'
  const versions: Array<IExperiment<string>> = []

  betaGroups
    // only participate in betas that have our slug or have no slugs
    .filter((group) => !group.slugs || group.slugs[slug])
    .forEach((group) => {
      versions.push({
        flag,
        percent: parseInt(group.trafficPercentage, 10),
        value: group.version
      })
    })

  versions.push({
    flag,
    default: true,
    value: 'production'
  })

  return versions
}

export function getVersionGroup(
  productionVersion: string,
  betas: Array<IBetaGroup>,
  slug: string
): IVersionGroup {
  // Above all else, if we're using an override query param,
  // throw everything out and run it instead.
  const override = getQueryParam('wrapperVersion') as string
  if (override) {
    return { version: override, name: 'local' }
  }

  if (window.$mediavine.web.model.versionGroup) {
    return window.$mediavine.web.model.versionGroup
  }
  // Start by creating Experiment objects out of the beta data
  // The results can be:
  // 1. A beta version
  // 2. 'production'
  // 3. null, if prod version or beta data isn't present (for local testing)
  const experiments = transformToIExperiments(productionVersion, betas, slug)
  const experiment = new Experiment(experiments)
  const version: string | null = experiment.activate<string>(
    ExperimentFlag.version
  )

  // Next, sort out the result of the experiment
  // The version group will contain which version to use
  // and what name to call it. Specifically, are we running
  // a -beta-control, a -beta-test, or just the prod version.
  let versionGroup: IVersionGroup

  if (!version || !productionVersion) {
    // We're running locally.
    versionGroup = { version: '', name: 'local' }
  } else if (version === 'production') {
    // Use the prod version for version and name
    versionGroup = { version: productionVersion, name: productionVersion }
  } else {
    // Determine if we want to run the test group or control group
    const fifty = 50
    const runBetaTest = Math.floor(Math.random() * 100) + 1 > fifty
    // Call it the correct name
    const name = runBetaTest
      ? `${version}-beta-test`
      : `${version}-beta-control`
    // Run the beta version or production version accordingly!
    const betaOrControlVersion = runBetaTest ? version : productionVersion
    versionGroup = { version: betaOrControlVersion, name }
  }

  return versionGroup
}

export function getVideoVersionGroup(
  controlVersion: string,
  betas: IVideoBetaGroup[]
): IVersionGroup {
  try {
    const getRoll = () => Math.floor(Math.random() * 100) + 1 // [1, 100]
    const chance = 50
    const control = getRoll() <= chance
    let groupRoll = getRoll()

    for (const { trafficPercentage, version } of betas) {
      if (groupRoll <= trafficPercentage) {
        return {
          name: control ? `${version}-beta-control` : `${version}-beta-test`,
          version: control ? controlVersion : version
        }
      }
      groupRoll = groupRoll - trafficPercentage
    }
  } catch (e) {
    console.error('Problem selecting group:', e)
  }

  return { name: 'default', version: controlVersion }
}
